import { MapEngine } from './engine';
import type { EngineHost } from './engine';
import type { MapStats, Orientation, Sector, Wash } from './controller';
import type { FlyMessage, FogMessage, InitMessage, WorkerOutbound } from './worker-protocol';
import type { RenderEdge, RenderNode } from '@versesverse/core';

/*
 * ONE HANDLE, TWO ENGINES. The map draws OFF the main thread whenever the browser can: an OffscreenCanvas
 * is transferred to a worker that owns the whole draw loop, so a pan never janks the UI. Where that is
 * unavailable (older Safari, jsdom in tests) it falls back to the same MapEngine on the main thread.
 * MapCanvas talks to whichever through this interface and never knows which it got — the only seam is
 * hitTest, which is async because the worker alone holds the geometry a tap resolves against.
 */
export interface MapEngineHandle {
  setViewport(width: number, height: number, dpr: number): void;
  pan(dx: number, dy: number): void;
  zoomAt(sx: number, sy: number, factor: number): void;
  focusOn(id: string, options: { animate: boolean; inset: { right: number; bottom: number } }): void;
  /**
   * The tour's camera: a world point to centre on — or null for the whole field — at a zoom that
   * is a multiple of the zoom that fits the field. `flyTo(null, 1, …)` is the whole map.
   */
  flyTo(to: { x: number; y: number } | null, zoom: number, durMs: number): void;
  setSelected(id: string | null): void;
  setInteracting(value: boolean): void;
  /** Where the chrome covers the map, so no name is placed under it. */
  setChrome(bands: { x0: number; y0: number; x1: number; y1: number }[]): void;
  /**
   * What the reader has walked and what stands one step from it.
   *
   * The map is something you uncover by walking rather than something you filter, and this is what
   * makes that true rather than merely said. An empty `walked` draws no fog — which is what a map
   * nobody has walked must look like.
   */
  setFog(walked: readonly string[], within: readonly string[], mix: number): void;
  hitTest(sx: number, sy: number): Promise<string | null>;
  stats(): MapStats | null;
  destroy(): void;
}

/** True when the map can be drawn on a worker thread — a transferable canvas, a worker, and OffscreenCanvas. */
function canOffscreen(canvas: HTMLCanvasElement): boolean {
  return typeof Worker !== 'undefined'
    && typeof OffscreenCanvas !== 'undefined'
    && typeof canvas.transferControlToOffscreen === 'function';
}

/** The worker path: forward every command as a message; resolve a tap through one round trip. */
class WorkerEngineHandle implements MapEngineHandle {
  private readonly worker: Worker;
  private readonly pending = new Map<number, (id: string | null) => void>();
  private lastStats: MapStats | null = null;
  /** The latest push of what the reader is looking at, and who to tell about the next one. */
  private readonly onOrientation?: (at: Orientation | null) => void;
  private reqSeq = 0;

  constructor(canvas: HTMLCanvasElement, nodes: RenderNode[], edges: RenderEdge[], washes: Wash[], sectors: Sector[], width: number, height: number, dpr: number, onOrientation?: (at: Orientation | null) => void) {
    this.onOrientation = onOrientation;
    this.worker = new Worker(new URL('./map.worker.ts', import.meta.url), { type: 'module' });
    this.worker.addEventListener('message', (event: MessageEvent<WorkerOutbound>) => {
      const message = event.data;
      if (message.type === 'hit') { this.pending.get(message.reqId)?.(message.id); this.pending.delete(message.reqId); }
      else if (message.type === 'stats') this.lastStats = message.stats;
      else if (message.type === 'orientation') this.onOrientation?.(message.at);
    });
    const offscreen = canvas.transferControlToOffscreen();
    /*
     * Typed as `InitMessage` on the way out, not left to `postMessage`'s `any`.
     *
     * The worker reads this object field by field, so a field added to the protocol and not sent
     * is a runtime `undefined` on the other thread and nothing on this one — which is exactly what
     * happened when the washes were added: the protocol required them, every workspace typechecked
     * clean, and the worker would have drawn no washes at all.
     */
    const init: InitMessage = { type: 'init', canvas: offscreen, nodes, edges, washes, sectors, width, height, dpr };
    this.worker.postMessage(init, [offscreen]);
  }

  setViewport(width: number, height: number, dpr: number): void { this.worker.postMessage({ type: 'viewport', width, height, dpr }); }
  pan(dx: number, dy: number): void { this.worker.postMessage({ type: 'pan', dx, dy }); }
  zoomAt(sx: number, sy: number, factor: number): void { this.worker.postMessage({ type: 'zoom', sx, sy, factor }); }
  focusOn(id: string, options: { animate: boolean; inset: { right: number; bottom: number } }): void {
    this.worker.postMessage({ type: 'focus', id, animate: options.animate, inset: options.inset });
  }
  flyTo(to: { x: number; y: number } | null, zoom: number, durMs: number): void {
    /* Typed on the way out for the same reason `init` is — `postMessage` takes `any`, so a field
       the worker reads and this forgets to send is a runtime `undefined` and nothing on this side. */
    const fly: FlyMessage = { type: 'fly', to, zoom, durMs };
    this.worker.postMessage(fly);
  }
  setSelected(id: string | null): void { this.worker.postMessage({ type: 'select', id }); }
  setInteracting(value: boolean): void { this.worker.postMessage({ type: 'interacting', value }); }
  setChrome(bands: { x0: number; y0: number; x1: number; y1: number }[]): void { this.worker.postMessage({ type: 'chrome', bands }); }
  setFog(walked: readonly string[], within: readonly string[], mix: number): void {
    const fog: FogMessage = { type: 'fog', walked: [...walked], within: [...within], mix };
    this.worker.postMessage(fog);
  }

  hitTest(sx: number, sy: number): Promise<string | null> {
    const reqId = ++this.reqSeq;
    return new Promise(resolve => {
      this.pending.set(reqId, resolve);
      this.worker.postMessage({ type: 'hittest', reqId, sx, sy });
    });
  }

  stats(): MapStats | null { return this.lastStats; }
  destroy(): void { this.pending.clear(); this.worker.terminate(); }
}

/** The fallback path: the same MapEngine on the main thread, driven by requestAnimationFrame. */
class LocalEngineHandle implements MapEngineHandle {
  private readonly canvas: HTMLCanvasElement;
  private readonly engine: MapEngine;

  constructor(canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D, nodes: RenderNode[], edges: RenderEdge[], washes: Wash[], sectors: Sector[], width: number, height: number, dpr: number, onOrientation?: (at: Orientation | null) => void) {
    this.canvas = canvas;
    const host: EngineHost = {
      now: () => performance.now(),
      schedule: callback => { requestAnimationFrame(callback); },
      ...(onOrientation ? { onOrientation } : {}),
    };
    this.engine = new MapEngine(ctx, nodes, edges, host, washes, sectors);
    this.sizeBitmap(width, height, dpr);
    this.engine.setViewport(width, height, dpr);
    this.engine.resetCamera();
  }

  private sizeBitmap(width: number, height: number, dpr: number): void {
    this.canvas.width = Math.max(1, Math.round(width * dpr));
    this.canvas.height = Math.max(1, Math.round(height * dpr));
  }

  setViewport(width: number, height: number, dpr: number): void {
    this.sizeBitmap(width, height, dpr);
    this.engine.setViewport(width, height, dpr);
  }
  pan(dx: number, dy: number): void { this.engine.pan(dx, dy); }
  zoomAt(sx: number, sy: number, factor: number): void { this.engine.zoomAt(sx, sy, factor); }
  focusOn(id: string, options: { animate: boolean; inset: { right: number; bottom: number } }): void { this.engine.focusOn(id, options); }
  flyTo(to: { x: number; y: number } | null, zoom: number, durMs: number): void { this.engine.flyTo(to, zoom, durMs); }
  setSelected(id: string | null): void { this.engine.setSelected(id); }
  setInteracting(value: boolean): void { this.engine.setInteracting(value); }
  setChrome(bands: { x0: number; y0: number; x1: number; y1: number }[]): void { this.engine.setChrome(bands); }
  setFog(walked: readonly string[], within: readonly string[], mix: number): void { this.engine.setFog(walked, within, mix); }
  hitTest(sx: number, sy: number): Promise<string | null> { return Promise.resolve(this.engine.hitTest(sx, sy)); }
  stats(): MapStats { return this.engine.stats(); }
  destroy(): void { this.engine.destroy(); }
}

/**
 * Build the map's draw engine for `canvas`, preferring the worker and falling back to the main thread.
 * Returns null only when there is no 2D context at all (jsdom), so the element still mounts inert.
 */
export function createEngineHandle(
  canvas: HTMLCanvasElement,
  nodes: RenderNode[],
  edges: RenderEdge[],
  washes: Wash[],
  sectors: Sector[],
  width: number,
  height: number,
  dpr: number,
  preferWorker = true,
  onOrientation?: (at: Orientation | null) => void,
): MapEngineHandle | null {
  if (preferWorker && canOffscreen(canvas)) {
    try {
      return new WorkerEngineHandle(canvas, nodes, edges, washes, sectors, width, height, dpr, onOrientation);
    } catch {
      /* transferControlToOffscreen can throw if the canvas already has a context; fall through. */
    }
  }
  /* getContext throws (not returns null) on a canvas already transferred to an OffscreenCanvas, so it is
     guarded: a re-init on a transferred canvas mounts inert rather than crashing React. */
  let ctx: CanvasRenderingContext2D | null = null;
  try { ctx = canvas.getContext('2d'); } catch { ctx = null; }
  if (!ctx) return null;
  return new LocalEngineHandle(canvas, ctx, nodes, edges, washes, sectors, width, height, dpr, onOrientation);
}
